import React from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Button, Grid, TextField, FormControlLabel, Checkbox } from "@mui/material";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";

import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDDLShipmentPendingListQuery } from "store/services/shipmentApi";
import { useLazyGetDDLDriverListQuery } from "store/services/driverApi";
import { useLazyGetDDLContractorListQuery } from "store/services/contractorApi";
import { useLazyGetDdlVendorProfileForCollectionQuery } from "store/services/collectionApi";

import { getDateTimeAuto } from "utils/dateFormats";
import { useAuth } from "hooks/useAuth";

// Utility function to format ISO date for datetime-local input
const formatDateTimeForInput = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const INITIAL_STATE = {
  collectionId: "",
  shipmentId: [],
  vehicle_id: "",
  driverId: "",
  contractorId: "",
  isActive: true,
  fromVendor: false,
  vendor: null,
};

const CollectionForm = ({
  isBusy = false,
  fulfillmentDetail = null,
  onSubmit = () => { },
  onVendorChange = () => { }, 
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  // RTK Queries
  const { data: accounts, isLoading: loadingAccounts } = useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [fetchShipments, { data: shipments, isLoading: loadingShipments }] =
    useLazyGetDDLShipmentPendingListQuery();
  const [fetchDrivers, { data: drivers, isLoading: loadingDrivers }] =
    useLazyGetDDLDriverListQuery();
  const [fetchContractors, { data: contractors, isLoading: loadingContractors }] =
    useLazyGetDDLContractorListQuery();
  const [fetchVendors, { data: vendorData, isLoading: loadingVendors }] =
    useLazyGetDdlVendorProfileForCollectionQuery();

  // Track whether vendors have been fetched
  const [vendorsFetched, setVendorsFetched] = React.useState(false);

  // React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      actualPickupTime: getDateTimeAuto(),
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account || "",
      status: "Completed",
    },
  });

  const _watchAccount = useWatch({ control, name: "account" });
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchContractor = useWatch({ control, name: "contractorId" });
  const _watchDriver = useWatch({ control, name: "driverId" });
  const _watchFromVendor = useWatch({ control, name: "fromVendor" });
  const _watchVendor = useWatch({ control, name: "vendor" });

  const selectedDrivers = _watchContractor?.driverId || [];
  const selectedVehicles =
    _watchContractor?.vehicle_id || _watchDriver?.assigned_vehicle_ids || [];

  // Notify parent of vendor change
  React.useEffect(() => {
    onVendorChange(_watchVendor);
  }, [_watchVendor, onVendorChange]);

  // Handle Form Submission
  const handleOnSubmit = (data) => {
    const formattedData = {
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      shipmentId: data?.shipmentId?.map((shipment) => shipment._id) || [],
      vehicle_id: data?.vehicle_id?._id,
      driverId: data?.driverId?._id,
      contractorId: data?.contractorId?._id,
      actualPickupTime: new Date(data.actualPickupTime).toISOString(),
    };
    onSubmit(formattedData);
  };

  // Populate form with fulfillmentDetail if available
  React.useEffect(() => {
    if (fulfillmentDetail) {
      const detailToReset = { ...fulfillmentDetail };
      if (detailToReset.actualPickupTime) {
        detailToReset.actualPickupTime = formatDateTimeForInput(
          detailToReset.actualPickupTime
        );
      }
      reset({ ...INITIAL_STATE, ...detailToReset });
    }
  }, [fulfillmentDetail, reset]);

  // Fetch entities based on account
  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin)
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id,
      });
  }, [
    _watchAccount?._id,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity?.entity?.account,
  ]);

  // Fetch contractors, drivers, and shipments based on account and entity
  React.useEffect(() => {
    if (!_watchEntity?._id) return;
    fetchContractors({
      account: _watchAccount?._id,
      entity: _watchEntity?._id,
    });
    fetchDrivers({ account: _watchAccount?._id, entity: _watchEntity?._id });
    if (!_watchFromVendor) {
      fetchShipments({ account: _watchAccount?._id, entity: _watchEntity?._id });
    }
  }, [_watchAccount?._id, _watchEntity?._id, _watchFromVendor, fetchContractors, fetchDrivers, fetchShipments]);

  // Fetch vendors and shipments when "From Vendor" is checked or vendor is selected
  React.useEffect(() => {
    if (!_watchEntity?._id) return;
    if (_watchFromVendor && !vendorsFetched) {
      fetchVendors({ account: _watchAccount?._id, entity: _watchEntity?._id });
      setVendorsFetched(true);
    } else if (_watchFromVendor && _watchVendor?._id) {
      fetchVendors({
        account: _watchAccount?._id,
        entity: _watchEntity?._id,
        vendorId: _watchVendor?._id,
      });
    }
  }, [_watchFromVendor, _watchVendor?._id, _watchAccount?._id, _watchEntity?._id, fetchVendors, vendorsFetched]);

  // Reset vendorsFetched when account or entity changes
  React.useEffect(() => {
    setVendorsFetched(false);
    setValue("vendor", null);
    setValue("shipmentId", []);
  }, [_watchAccount?._id, _watchEntity?._id, setValue]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {/* From Vendor Checkbox */}
          <Grid item xs={12} mt={-3}>
            <Controller
              name="fromVendor"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} />}
                  label="From Vendor"
                />
              )}
            />
          </Grid>
          {(isAccountAdmin || isSystemAdmin) && (
            <>
              <Grid item md={6}>
                <Controller
                  name="account"
                  control={control}
                  rules={{ required: "Please select an account." }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      {...field}
                      fullWidth
                      label="Account"
                      required
                      value={field.value ?? ""}
                      onChange={(_, data) => {
                        field.onChange(data);
                        setValue("entity", "");
                        setValue("contractorId", "");
                        setValue("driverId", "");
                        setValue("vehicle_id", "");
                      }}
                      getOptionLabel={(option) => option?.name ?? ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={accounts || []}
                      loading={loadingAccounts}
                      loadingText="Loading Account..."
                      disabled
                      helperText={errors?.account?.message}
                      error={!!errors?.account}
                    />
                  )}
                />
              </Grid>
              <Grid item md={6}>
                <Controller
                  name="entity"
                  control={control}
                  rules={{ required: "Please select an entity." }}
                  render={({ field: { value, onChange } }) => (
                    <BaseAutocomplete
                      fullWidth
                      required
                      label="Entity"
                      value={value ?? ""}
                      onChange={(_, data) => {
                        onChange(data);
                        setValue("contractorId", "");
                        setValue("driverId", "");
                        setValue("vehicle_id", "");
                      }}
                      getOptionLabel={(option) => option?.name ?? ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={entities || []}
                      loading={loadingEntities}
                      loadingText="Loading Entities..."
                      helperText={errors?.entity?.message}
                      error={!!errors?.entity}
                      disabled
                    />
                  )}
                />
              </Grid>
            </>
          )}

          {/* Vendor Selection */}
          {_watchFromVendor && (
            <Grid item md={6}>
              <Controller
                name="vendor"
                control={control}
                rules={{ required: "Please select a vendor." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Vendor Profile"
                    required
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("shipmentId", []);
                    }}
                    getOptionLabel={(option) =>
                      option && `${option?.shopName} - ${option?.ownerName}`
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={vendorData?.vendors || []}
                    loading={loadingVendors}
                    loadingText="Loading Vendors..."
                    helperText={errors?.vendor?.message}
                    error={!!errors?.vendor}
                  />
                )}
              />
            </Grid>
          )}

          {/* Shipment ID */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{
                validate: (value) =>
                  value?.length > 0 ||
                  "Please select at least one Shipment ID.",
              }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  multiple
                  label="Shipment ID"
                  required
                  value={value || []}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) => {
                    if (!option?.shipmentId) return "";
                    const destCode = option?.destinationBranch?.code?.trim() || "";
                    const label =
                      option.status === "Return"
                        ? `${option.shipmentId} (R)`
                        : option.shipmentId;
                    return `${label} - ${destCode}`;
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={
                    _watchFromVendor && _watchVendor
                      ? vendorData?.shipments || []
                      : shipments || []
                  }
                  loading={loadingShipments || loadingVendors}
                  loadingText="Loading Shipments..."
                  disabled={!!fulfillmentDetail}
                  helperText={errors?.shipmentId?.message}
                  error={!!errors?.shipmentId}
                />
              )}
            />
          </Grid>

          {/* Contractor ID */}
          <Grid item md={6}>
            <Controller
              name="contractorId"
              control={control}
              rules={{ required: "Please select Contractor ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Contractor Profile"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);
                    setValue("driverId", "");
                    setValue("vehicle_id", "");
                  }}
                  getOptionLabel={(option) =>
                    option && `${option?.contractorId} - ${option?.name}`
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={contractors || []}
                  loading={loadingContractors}
                  loadingText="Loading Contractors..."
                  helperText={errors?.contractorId?.message}
                  error={!!errors?.contractorId}
                />
              )}
            />
          </Grid>

          {/* Driver ID */}
          <Grid item md={6}>
            <Controller
              name="driverId"
              control={control}
              rules={{ required: "Please select Driver ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Driver"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);
                    setValue("vehicle_id", "");
                  }}
                  getOptionLabel={(option) =>
                    option && `${option?.driverId} - ${option?.name}`
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={
                    selectedDrivers.length > 0 ? selectedDrivers : drivers || []
                  }
                  loading={loadingDrivers}
                  loadingText="Loading Drivers..."
                  helperText={errors?.driverId?.message}
                  error={!!errors?.driverId}
                  disabled={!_watchContractor}
                />
              )}
            />
          </Grid>

          {/* Vehicle ID */}
          <Grid item md={6}>
            <Controller
              name="vehicle_id"
              control={control}
              rules={{ required: "Please select Vehicle ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Vehicle"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) =>
                    option && option.vehicle_id && option.registration_number
                      ? `${option.vehicle_id} - ${option.registration_number}`
                      : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    typeof option === "object" && typeof value === "object"
                      ? option._id === value._id
                      : option === value
                  }
                  options={selectedVehicles}
                  disabled={!_watchDriver}
                  loadingText="Loading Vehicles..."
                  helperText={errors?.vehicle_id?.message}
                  error={!!errors?.vehicle_id}
                />
              )}
            />
          </Grid>

          {/* Actual Pickup Time */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="actualPickupTime"
              control={control}
              rules={{ required: "Actual Pickup Time is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  type="datetime-local"
                  label="Actual Pickup Time"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.actualPickupTime}
                  helperText={errors.actualPickupTime?.message}
                />
              )}
            />
          </Grid>

          {/* Is Active */}
          {fulfillmentDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit(handleOnSubmit)}>
          {fulfillmentDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default CollectionForm;